import { seriesCategories, seriesEras, seriesRegions } from "@/mocks/series";

export interface FilterState {
  era: string;
  categories: string[];
  regions: string[];
  sort: string;
}

interface FilterPanelProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  total: number;
}

const sortOptions = [
  { value: "hot", label: "按热度" },
  { value: "year", label: "按首播时间" },
  { value: "rating", label: "按评分" },
];

export default function FilterPanel({ value, onChange, onReset, total }: FilterPanelProps) {
  const isActive =
    value.era !== "" || value.categories.length > 0 || value.regions.length > 0;

  const toggleArray = (key: "categories" | "regions", item: string) => {
    const list = value[key];
    const next = list.includes(item)
      ? list.filter((entry) => entry !== item)
      : [...list, item];
    onChange({ ...value, [key]: next });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3">
        <div>
          <p className="text-xs text-foreground-500">当前筛选结果</p>
          <p className="font-heading text-lg font-bold text-primary-700">{total} 部剧集</p>
        </div>
        <button
          type="button"
          onClick={onReset}
          disabled={!isActive}
          className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="ri-refresh-line"></i>
          重置筛选
        </button>
      </div>

      <div>
        <h3 className="font-heading text-sm font-semibold text-foreground-900">排序方式</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange({ ...value, sort: option.value })}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                value.sort === option.value
                  ? "bg-secondary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-secondary-400"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-sm font-semibold text-foreground-900">剧集年代</h3>
        <div className="mt-3 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => onChange({ ...value, era: "" })}
            className={`whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition-colors ${
              value.era === ""
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            全部年代
          </button>
          {seriesEras.map((era) => (
            <button
              key={era.label}
              type="button"
              onClick={() => onChange({ ...value, era: era.label })}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition-colors ${
                value.era === era.label
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {era.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-sm font-semibold text-foreground-900">副分类</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {seriesCategories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => toggleArray("categories", category)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                value.categories.includes(category)
                  ? "bg-accent-500 text-primary-950"
                  : "border border-background-300 text-foreground-700 hover:border-accent-400"
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-sm font-semibold text-foreground-900">地区</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {seriesRegions.map((region) => (
            <button
              key={region}
              type="button"
              onClick={() => toggleArray("regions", region)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                value.regions.includes(region)
                  ? "bg-accent-500 text-primary-950"
                  : "border border-background-300 text-foreground-700 hover:border-accent-400"
              }`}
            >
              {region}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}